// Greenhold <DossierShopCard> — public-shop product card (Story 2.7).
//
// FIRST `composed/*` component to ship. Per architecture L2458-L2459:
//
//   * `greenhold/*` — bespoke artifacts (Certificate, SealedDossier,
//     TierBadge). Story 2.7 ships SealedDossier (v1 preview state).
//   * `composed/*` — higher-order combinations of `greenhold/*` +
//     `ui/*`. This card composes SealedDossier + Card + price/CTA footer.
//
// UX spec §3.5 (L1495-L1524) is the canonical reference. Story 2.7
// realizes the `regular` and `special` variants; `welcome` is accepted
// in the type union for forward-compat (the welcome activate flow in
// Story 2.5's `/admin/welcome-portfolio` page COULD render a preview
// of the welcome drop using this component) but the public shop NEVER
// renders welcome — Story 2.5 AC6 + shop_service exclude
// `is_perpetual=true` rows.
//
// Epic 8 design pass (2026-08): the card no longer surfaces the tier-odds
// dots row or the "N creators · …" line (per visual review — the shop
// doesn't advertise tier probabilities, and the creator roster isn't the
// card's job). The portfolio THEME (e.g. "Counter-Strike") now prints on
// the parchment via the SealedDossier `game` note; when a drop has no
// theme, nothing is shown in its place.

import type { FC } from "react";
import { Card } from "../ui/card";
import { Separator } from "../ui/separator";
import { SealedDossier } from "../greenhold/SealedDossier";
import { cn } from "../lib/utils";

export type DossierShopCardKind = "regular" | "special" | "welcome";
export type DossierShopCardStatus = "live" | "retiring";

export interface DossierShopCardDrop {
  drop_id: string;
  portfolio_title: string;
  portfolio_theme?: string | null;
  /** Wax-seal base color (migration 0079); absent/null = default bordeaux. */
  portfolio_seal_color?: string | null;
  kind: DossierShopCardKind;
  status: DossierShopCardStatus;
  price_cents: number;
  per_user_cap?: number | null;
  creator_count: number;
  first_creator_names: string[];
  retiring_at?: string | null;
}

export interface DossierShopCardProps {
  drop: DossierShopCardDrop;
  /**
   * Variant per UX-DR17. Defaults to `drop.kind`. The `welcome`
   * variant is accepted for forward-compat (admin preview surfaces);
   * the public shop never renders welcome.
   */
  variant?: DossierShopCardKind;
  /**
   * Auto-derived from `drop.status === "retiring"` if not explicitly
   * set. Set explicitly to render the badge in component-prototype
   * fixtures regardless of status.
   */
  showRetirementWarning?: boolean;
  /**
   * Click-target href — defaults to `/shop/${drop.drop_id}`. Story 2.8's
   * detail page is the canonical destination; until 2.8 ships, the
   * URL 404s gracefully (acceptable v1 behavior because Story 2.7
   * ships immediately before 2.8 lands).
   */
  href?: string;
}

const EUR_FORMATTER = new Intl.NumberFormat("en-IE", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
});

function formatEur(cents: number): string {
  return EUR_FORMATTER.format(cents / 100);
}

export const DossierShopCard: FC<DossierShopCardProps> = ({
  drop,
  variant,
  showRetirementWarning,
  href,
}) => {
  const resolvedVariant = variant ?? drop.kind;
  const showWarning = showRetirementWarning ?? drop.status === "retiring";
  const targetHref = href ?? `/shop/${drop.drop_id}`;

  const theme = drop.portfolio_theme?.trim() || null;
  const price = formatEur(drop.price_cents);

  const ariaLabel = [
    `Sealed dossier: ${drop.portfolio_title}`,
    price,
    resolvedVariant,
    theme ? `theme ${theme}` : null,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <a
      href={targetHref}
      aria-label={ariaLabel}
      className={cn(
        "group block no-underline focus-visible:outline-none",
        "focus-visible:ring-2 focus-visible:ring-brand-forest-500 focus-visible:ring-offset-2 rounded-lg",
      )}
    >
      <Card
        className={cn(
          "flex flex-col gap-4 p-4 transition-transform transition-shadow",
          "group-hover:-translate-y-0.5 group-hover:shadow-lg",
          "motion-reduce:transform-none",
        )}
      >
        {/* SealedDossier preview artifact — the theme prints bottom-left on
            the parchment via the `game` note (Epic 8: replaced the tier-odds
            + creator-count copy the shop no longer surfaces). */}
        <div className="flex justify-center">
          <SealedDossier
            title={drop.portfolio_title}
            game={theme ?? undefined}
            size="md"
            showRetirementWarning={showWarning}
            sealColor={drop.portfolio_seal_color}
          />
        </div>

        {/* Body — title only. The theme prints on the parchment (via the
            SealedDossier `game` note); tier odds and the creator-count line
            are deliberately not shown (Epic 8 design pass). */}
        <div className="flex flex-col gap-1">
          <h3 className="font-heading font-semibold text-base leading-snug line-clamp-2">
            {drop.portfolio_title}
          </h3>
        </div>

        <Separator />

        {/* Footer — price + Buy CTA. Inner button is decorative
            (parent <a> is the canonical interaction target — using a
            nested <button> would be invalid HTML). */}
        <div className="flex items-center justify-between gap-3">
          <span className="font-heading font-bold text-2xl">{price}</span>
          <span
            aria-hidden="true"
            className={cn(
              "inline-flex items-center justify-center rounded-md",
              "bg-brand-forest-700 text-brand-paper-100 font-semibold",
              "px-4 py-2 text-sm",
              "group-hover:bg-brand-forest-800 transition-colors",
            )}
          >
            Buy sealed →
          </span>
        </div>
      </Card>
    </a>
  );
};
